package ai.wagemix.components.layouts

import ai.wagemix.SiteLogo
import ai.wagemix.ThemeToggle
import ai.wagemix.siteStrings
import ai.wagemix.web.components.WMBandChart
import ai.wagemix.web.components.WMBandChartModel
import ai.wagemix.web.components.WMBandChartPoint
import ai.wagemix.web.components.WMBadge
import ai.wagemix.web.components.WMBadgeKind
import ai.wagemix.web.components.WMRaisedStyle
import ai.wagemix.web.components.of
import ai.wagemix.web.theme.WMAuthContentMaxWidthVar
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardPaddingVar
import ai.wagemix.web.theme.WMCardStackGapVar
import ai.wagemix.web.theme.WMHeadingGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMPanelCornerVar
import ai.wagemix.web.theme.WMSectionContentGapCompactVar
import ai.wagemix.web.theme.WMSectionTitleTextStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import org.jetbrains.compose.web.css.FlexWrap
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

val AuthBrandPanelStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    FSBreakpoint.Large {
        Modifier.display(DisplayStyle.Flex).width(50.percent).flexGrow(1).minHeight(100.vh).padding(64.px)
            .gap(WMSectionContentGapCompactVar.value())
            .backgroundColor(colorMode.toPalette().fs.primary).color(colorMode.toPalette().fs.onPrimary)
    }
}

/** The form side: half the window from Large up (the brand panel takes the other half), the whole window below. */
val AuthFormSideStyle = CssStyle {
    base { Modifier.fillMaxWidth().minHeight(100.vh).padding(24.px).gap(24.px) }
    FSBreakpoint.Large { Modifier.width(50.percent).padding(48.px) }
}

/**
 * The form's width follows the window, up to [WMAuthContentMaxWidthVar]: the full width of the form side on phones,
 * 80% of it on tablets and 75% of it from Large up.
 */
val AuthFormStyle = CssStyle {
    base { Modifier.fillMaxWidth().maxWidth(WMAuthContentMaxWidthVar.value()) }
    FSBreakpoint.Medium { Modifier.width(80.percent) }
    FSBreakpoint.Large { Modifier.width(75.percent) }
}

/** Penpot `preview`: the sample mix card on the brand panel (surface, panel corner, raised shadow). */
val AuthBrandPreviewStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier.fillMaxWidth().maxWidth(WMAuthContentMaxWidthVar.value()).padding(WMCardPaddingVar.value())
        .gap(WMCardStackGapVar.value()).borderRadius(WMPanelCornerVar.value())
        .backgroundColor(colors.surface).color(colors.onSurface)
}

/** The brand copy under the preview, centred and as wide as the preview at most. */
val AuthBrandCopyStyle = CssStyle.base {
    Modifier.fillMaxWidth().maxWidth(WMAuthContentMaxWidthVar.value()).gap(WMHeadingGapVar.value()).textAlign(TextAlign.Center)
}

/**
 * Penpot `Auth / …`: the form side on the left (logo and theme toggle across the top, the form centred vertically, the
 * legal line at the bottom) and the primary brand panel on the right (the sample mix preview card and the brand copy,
 * centred vertically).
 */
@Layout
@Composable
fun AuthLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val s = siteStrings
    val au = s.auth
    val colors = ColorMode.current.toPalette().fs
    Row(modifier = Modifier.fillMaxWidth().minHeight(100.vh).backgroundColor(colors.background)) {
        Column(AuthFormSideStyle.toModifier()) {
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SiteLogo()
                Box(Modifier.fillMaxWidth())
                ThemeToggle()
            }
            Column(
                modifier = Modifier.fillMaxWidth().flexGrow(1),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(AuthFormStyle.toModifier()) { content() }
            }
            Row(modifier = Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                val caption = WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant)
                SpanText(au.copyright, caption)
                SpanText("·", caption)
                Link("/terms", au.termsOfUse, modifier = caption, variant = UndecoratedLinkVariant)
                SpanText("·", caption)
                Link("/privacy-policy", au.privacyPolicy, modifier = caption, variant = UndecoratedLinkVariant)
            }
        }
        Column(
            AuthBrandPanelStyle.toModifier(),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(AuthBrandPreviewStyle.toModifier().then(WMRaisedStyle.toModifier())) {
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(au.previewTitle, WMLabelStrongTextStyle.toModifier().fillMaxWidth())
                    WMBadge(au.previewBadge, kind = WMBadgeKind.AI)
                }
                WMBandChart(model = sampleChartModel(), description = s.mixBuilder.chartDescription)
            }
            Column(AuthBrandCopyStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
                SpanText(au.brandHeadline, WMSectionTitleTextStyle.toModifier())
                SpanText(au.brandLead, WMBodyTextStyle.toModifier())
            }
        }
    }
}

/** Static illustrative data for the brand panel and the landing hero. */
fun sampleChartModel(): WMBandChartModel = WMBandChartModel.of(
    points = listOf(
        WMBandChartPoint.of(2020, 38_200.0, 46_900.0, 58_400.0, 72_100.0, 89_300.0),
        WMBandChartPoint.of(2021, 39_100.0, 48_000.0, 59_900.0, 74_000.0, 91_600.0),
        WMBandChartPoint.of(2022, 40_800.0, 50_200.0, 62_700.0, 77_400.0, 95_900.0),
        WMBandChartPoint.of(2023, 42_600.0, 52_500.0, 65_600.0, 81_000.0, 100_300.0),
        WMBandChartPoint.of(2024, 44_100.0, 54_400.0, 68_000.0, 84_000.0, 104_000.0),
        WMBandChartPoint.of(2025, 45_700.0, 56_400.0, 70_500.0, 87_100.0, 107_800.0),
        WMBandChartPoint.of(2026, 47_300.0, 58_400.0, 73_000.0, 90_200.0, 111_700.0)
    ),
    // As in the builder: the latest BLS release covers 2025 and one year is projected.
    lastActualYear = 2025,
    hourly = false
)
